import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { PriceDisplay } from '../price/PriceDisplay';
import { useCallback, useId, useRef } from 'react';
import { SingleSkeletonLoader } from '../loading/SingleSkeletonLoader';
import { parseFscSeatStepperValue } from './parseFscSeatStepperValue';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const FscSeatTypeRow = ({
  option,
  currencyCode,
  pricesLoading = false,
  onQuantityChange
}) => {
  const nameId = useId();
  const {
    seatTypeId,
    name,
    description,
    stepper,
    pricePerSeat,
    ownedQuantity
  } = option;
  const commit = useCallback(rawValue => {
    const value = parseFscSeatStepperValue(rawValue, stepper.min, stepper.max);
    if (value === undefined) {
      return;
    }
    onQuantityChange(seatTypeId, value);
  }, [onQuantityChange, seatTypeId, stepper.max, stepper.min]);

  /*
   * Distinguishes typing from +/- stepping. StepperInput.handleStep fires
   * onChange then onStep synchronously, so a step ends with hasTyped=false.
   * Skipping the commit on the transient blur a +/- click triggers (focus
   * briefly leaves the input for the button) keeps a typed draft from being
   * restored over the stepped quantity. Cart updates always allocate a new
   * object, so that blur is not a no-op even when the parsed value matches.
   */
  const hasTypedRef = useRef(false);
  const handleStepperChange = useCallback(() => {
    hasTypedRef.current = true;
  }, []);
  const handleStepperStep = useCallback(evt => {
    hasTypedRef.current = false;
    commit(String(evt.target.value));
  }, [commit]);
  const handleStepperBlur = useCallback(evt => {
    if (!hasTypedRef.current) {
      return;
    }
    hasTypedRef.current = false;
    commit(evt.target.value);
  }, [commit]);
  const handleStepperKeyDown = useCallback(evt => {
    if (evt.key === 'Enter') {
      evt.currentTarget.blur();
    }
  }, []);
  return /*#__PURE__*/_jsxs(Flex, {
    justifyContent: "space-between",
    alignItems: "start",
    gap: "400",
    children: [/*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "p",
        id: nameId,
        typography: "heading-50",
        children: name
      }), pricesLoading ? /*#__PURE__*/_jsx(SingleSkeletonLoader, {
        height: 14,
        width: "96px"
      }) : /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "detail-200",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "fscConfigure.seatsCard.perSeatPerMonth",
          options: {
            price: /*#__PURE__*/_jsx(PriceDisplay, {
              price: pricePerSeat,
              currencyCode: currencyCode
            })
          }
        })
      }), description && /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "body-100",
        color: "text-core-subtle",
        marginBlockStart: "100",
        children: description
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "end",
      gap: "100",
      children: [/*#__PURE__*/_jsx(StepperInput, {
        value: stepper.quantity,
        onChange: handleStepperChange,
        onStep: handleStepperStep,
        onBlur: handleStepperBlur,
        onKeyDown: handleStepperKeyDown,
        min: stepper.min,
        max: stepper.max,
        stepSize: 1,
        inlineSize: "115px",
        "aria-labelledby": nameId,
        "data-test-id": `${seatTypeId}-seat-stepper-input`
      }), ownedQuantity > 0 && /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "detail-200",
        color: "text-core-subtle",
        "data-test-id": `${seatTypeId}-owned-seats`,
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "fscConfigure.seatsCard.ownedSeats",
          options: {
            count: ownedQuantity
          }
        })
      })]
    })]
  });
};